<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <b-row align-v="center">
          <b-col cols="12" md="auto" class="mb-1 mb-md-0">
            <b-form-radio-group
              v-model="selectedCluster"
              :options="clusterOptions"
              buttons
              button-variant="outline-primary"
              size="sm"
            />
          </b-col>
          <b-col cols="12" md="6" lg="5" class="mb-1 mb-md-0">
            <v-select
              v-model="selectedPack"
              :options="packs"
              :reduce="p => p.id"
              :get-option-label="packLabel"
              placeholder="Затемнить тех, кто играл этот пакет…"
              :clearable="true"
            />
          </b-col>
          <b-col v-if="period" cols="12" md="auto" class="text-muted small">
            Период: {{ period.from }} — {{ period.to }}
          </b-col>
          <b-col cols="12" md="auto" class="ml-md-auto mb-1 mb-md-0">
            <b-button
              variant="primary"
              :disabled="!players.length || isExporting"
              @click="onExport"
            >
              <b-spinner v-if="isExporting" small class="mr-50" />
              <feather-icon v-else icon="DownloadIcon" class="mr-50" />
              Скачать картинку
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-overlay :show="isLoading" rounded="sm">
      <b-card no-body>
        <b-card-body>
          <b-table
            :items="players"
            :fields="fields"
            :tbody-tr-class="rowClass"
            empty-text="Нет игроков с играми за период"
            show-empty
            small
            striped
            hover
          />
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BTable, BFormRadioGroup, BOverlay, BRow, BCol, BButton, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  ref, watch, onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import playersStoreModule from '@/views/players/playersStoreModule'
import exportRatingImage from '@/views/players/exportRatingImage'

const STORE_NAMESPACE = 'app-players'

export default {
  components: {
    BCard, BCardBody, BTable, BFormRadioGroup, BOverlay, BRow, BCol, BButton, BSpinner, vSelect,
  },
  setup() {
    if (!store.hasModule(STORE_NAMESPACE)) {
      store.registerModule(STORE_NAMESPACE, playersStoreModule)
    }
    onUnmounted(() => {
      if (store.hasModule(STORE_NAMESPACE)) store.unregisterModule(STORE_NAMESPACE)
    })

    const toast = useToast()

    const clusterOptions = [
      { text: 'Музыка', value: 'music' },
      { text: 'Кино', value: 'movies' },
      { text: 'Другое', value: 'other' },
    ]

    const selectedCluster = ref('music')
    const selectedPack = ref(null)
    const players = ref([])
    const packs = ref([])
    const period = ref(null)
    const isLoading = ref(false)
    const isExporting = ref(false)

    const fields = [
      { key: 'name', label: 'Игрок' },
      { key: 'rating', label: 'Рейтинг', class: 'text-right' },
      { key: 'games', label: 'Игры', class: 'text-right' },
      { key: 'wins', label: 'Победы', class: 'text-right' },
      { key: 'points', label: 'Баллы', class: 'text-right' },
    ]

    const fetchRating = () => {
      isLoading.value = true
      store.dispatch(`${STORE_NAMESPACE}/fetchRating`, { cluster: selectedCluster.value })
        .then(response => {
          const data = response.data || {}
          players.value = data.players || []
          packs.value = data.packs || []
          period.value = data.period || null
          selectedPack.value = null
        })
        .catch(err => {
          toast({
            component: ToastificationContent,
            props: {
              title: err.response?.data?.message || 'Ошибка загрузки рейтинга',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const rowClass = item => {
      if (!item) return ''
      if (selectedPack.value && Array.isArray(item.played_pack_ids)
        && item.played_pack_ids.includes(selectedPack.value)) {
        return 'players-rating-played'
      }
      return ''
    }

    const packLabel = p => {
      if (!p) return ''
      const date = p.premiere_date ? ` (${p.premiere_date})` : ''
      return `${p.name}${date}`
    }

    const onExport = async () => {
      if (!players.value.length) return
      isExporting.value = true
      try {
        await exportRatingImage({
          players: players.value,
          cluster: selectedCluster.value,
        })
      } catch (err) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Не удалось сгенерировать картинку',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isExporting.value = false
      }
    }

    watch(selectedCluster, fetchRating)
    fetchRating()

    return {
      clusterOptions,
      selectedCluster,
      selectedPack,
      players,
      packs,
      period,
      isLoading,
      isExporting,
      onExport,
      fields,
      rowClass,
      packLabel,
    }
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';

.players-rating-played > td {
  opacity: 0.4;
}
</style>
